import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { waLink } from "@/config";

const LINES = ["Ração boa,", "terra forte,", "bicho feliz."];

const HERO_IMAGE =
  "https://images.pexels.com/photos/4725939/pexels-photo-4725939.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 sm:pt-40 lg:pb-28"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-ochre/15 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-40 h-[26rem] w-[26rem] rounded-full bg-olive/10 blur-3xl"
      />

      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 sm:px-12 lg:grid-cols-12 lg:gap-8 lg:px-20">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-linen bg-white/60 px-4 py-2 backdrop-blur"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-clay opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-clay" />
            </span>
            <span className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-olive">
              Petshop & Produtos Agro
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 2 ? (
                    <>
                      bicho <em className="not-italic text-clay">feliz.</em>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-moss sm:text-lg"
          >
            Casa de rações e produtos para pet e campo. Linha completa de nutrição premium,
            acessórios, produtos veterinários e suprimentos agrícolas — com o atendimento de
            quem entende da terra e dos bichos.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#catalogo"
              data-testid="hero-catalog-button"
              className="group inline-flex items-center gap-3 rounded-full bg-olive px-7 py-4 text-sm font-semibold text-parchment transition-colors duration-300 hover:bg-forest"
            >
              Explorar o Catálogo
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </a>
            <a
              href={waLink("Olá, Agro Carmo! Vim pelo site e quero fazer um pedido.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="group inline-flex items-center gap-3 rounded-full border-2 border-clay px-7 py-[14px] text-sm font-semibold text-clay transition-colors duration-300 hover:bg-clay hover:text-parchment"
            >
              <MessageCircle className="h-4 w-4" />
              Pedir no WhatsApp
            </a>
          </motion.div>

          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-14 grid max-w-lg grid-cols-2 gap-6 border-t border-linen pt-8"
          >
            {[
              ["300+", "produtos na prateleira"],
              ["100%", "entrega na região"],
            ].map(([value, label]) => (
              <div key={label}>
                <dt className="sr-only">{label}</dt>
                <dd className="font-display text-2xl font-semibold text-olive sm:text-3xl">{value}</dd>
                <dd className="mt-1 text-xs text-moss sm:text-sm">{label}</dd>
              </div>
            ))}
          </motion.dl>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-[2.5rem] rounded-tr-[6rem] border border-linen shadow-2xl shadow-olive/20">
              <motion.img
                style={{ y: imageY, scale: 1.15 }}
                src={HERO_IMAGE}
                alt="Cão feliz no campo ao pôr do sol"
                data-testid="hero-image"
                className="h-[26rem] w-full object-cover sm:h-[30rem] lg:h-[34rem]"
                loading="eager"
              />
            </div>

            <div
              aria-hidden="true"
              className="absolute -bottom-8 -left-8 hidden h-36 w-36 items-center justify-center sm:flex"
            >
              <svg viewBox="0 0 100 100" className="animate-spin-slower absolute inset-0 h-full w-full">
                <defs>
                  <path id="stamp-circle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
                </defs>
                <text className="fill-olive font-mono text-[9px] uppercase" letterSpacing="2.5">
                  <textPath href="#stamp-circle">agro carmo · pet &amp; campo · agropecuária ·</textPath>
                </text>
              </svg>
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-clay shadow-lg">
                <svg width="26" height="26" viewBox="0 0 64 64" aria-hidden="true">
                  <path d="M32 52V28" stroke="#F7F4EE" strokeWidth="4" strokeLinecap="round" />
                  <path d="M32 38c-9 .5-14.5-5-15.5-14 9-.5 14.5 5 15.5 14z" fill="#F7F4EE" />
                  <path d="M32 31c9 .5 14.5-5 15.5-14-9-.5-14.5 5-15.5 14z" fill="#F7F4EE" opacity="0.7" />
                </svg>
              </span>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              className="animate-float-soft absolute -top-5 right-6 rounded-2xl border border-linen bg-parchment/95 px-5 py-3 shadow-xl backdrop-blur"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-moss">Casa de rações</p>
              <p className="font-display text-lg font-semibold text-ink">&amp; tudo para o campo</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
